import React, { useRef, useCallback, useEffect } from "react";
import { Avatar } from "../ui/components/Avatar";
import { Badge } from "../ui/components/Badge";
import { Button } from "../ui/components/Button";
import { IconButton } from "../ui/components/IconButton";
import { Table } from "../ui/components/Table";
import { Drawer } from "../ui/components/Drawer";
import { API_BASE_URL } from "../config/api";
import {
  FeatherAlertTriangle,
  FeatherClock,
  FeatherCreditCard,
  FeatherDownload,
  FeatherEye,
  FeatherFileText,
  FeatherX,
} from "@subframe/core";
import {
  getMetrics,
  getDistribution,
  getOverdueVendors,
  getDueSoonBills,
  getVendorAdvances,
  APAgingMetrics,
  APAgingBucket,
  APAgingDistributionResponse,
  OverdueVendor,
  DueSoonBill,
  VendorAdvance,
} from "../services/apAging";
import { formatPlAmount } from "../features/client_dashboard/presentation/utils/formatPlAmount";

function formatCurrencyForCSV(value: number | null | undefined): string {
  if (value === null || value === undefined) return "0";
  // Format as plain number with 2 decimal places, using Indian number format
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatRawAmountWithCommas(
  amount: number,
  currencyCode: string,
  currencySymbol: string,
): string {
  const locale =
    currencyCode.toUpperCase() === "USD" || currencyCode.toUpperCase() === "AED"
      ? "en-US"
      : "en-IN";
  const s = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${currencySymbol}${s}`;
}

function formatDateDdMmYyyy(value: string | null | undefined): string {
  if (!value) return "-";

  // Fast path for ISO-like dates: 2026-04-28 (optionally with time after)
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    return `${dd}/${mm}/${yyyy}`;
  }

  // Fallback for other date strings
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB"); // dd/mm/yyyy
}

type VendorBill = {
  billNumber: string;
  date: string;
  dueDate: string;
  amount: number;
  balance: number;
  status: string;
  daysOverdue: number;
};

export default function APAgingPage() {
  const asOfToday = React.useMemo(
    () =>
      new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    [],
  );
  const [metrics, setMetrics] = React.useState<APAgingMetrics[]>([]);
  const [distribution, setDistribution] = React.useState<APAgingBucket[]>([]);
  const [distributionSummary, setDistributionSummary] = React.useState<
    Record<
      string,
      Pick<
        APAgingDistributionResponse,
        | "asOfDate"
        | "totalOutstandingAmount"
        | "overdueAmount"
        | "notOverdueAmount"
        | "currencySymbol"
      >
    >
  >({});
  const [overdueVendors, setOverdueVendors] = React.useState<OverdueVendor[]>(
    [],
  );
  const [dueSoonBills, setDueSoonBills] = React.useState<DueSoonBill[]>([]);
  const [vendorAdvances, setVendorAdvances] = React.useState<VendorAdvance[]>(
    [],
  );
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  // Pagination state for overdue vendors
  const [overdueVendorsOffset, setOverdueVendorsOffset] =
    React.useState<number>(0);
  const [overdueVendorsHasMore, setOverdueVendorsHasMore] =
    React.useState<boolean>(false);
  const [overdueVendorsTotal, setOverdueVendorsTotal] =
    React.useState<number>(0);
  const OVERDUE_VENDORS_LIMIT = 10;

  // Pagination state for due soon bills
  const [dueSoonBillsOffset, setDueSoonBillsOffset] = React.useState<number>(0);
  const [dueSoonBillsHasMore, setDueSoonBillsHasMore] =
    React.useState<boolean>(false);
  const [dueSoonBillsTotal, setDueSoonBillsTotal] = React.useState<number>(0);
  const DUE_SOON_BILLS_LIMIT = 10;

  // Drawer state
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  const [selectedVendorId, setSelectedVendorId] = React.useState<string>("");
  const [selectedVendorName, setSelectedVendorName] =
    React.useState<string>("");
  const [billCurrencySymbol, setBillCurrencySymbol] =
    React.useState<string>("₹");
  const [vendorBills, setVendorBills] = React.useState<VendorBill[]>([]);
  const [billsLoading, setBillsLoading] = React.useState<boolean>(false);
  const [billsLoadingMore, setBillsLoadingMore] =
    React.useState<boolean>(false);
  const [billsHasMore, setBillsHasMore] = React.useState<boolean>(false);
  const [billsOffset, setBillsOffset] = React.useState<number>(0);
  const [billsTotal, setBillsTotal] = React.useState<number>(0);

  const BILLS_PAGE_SIZE = 20;
  const billsScrollRef = useRef<HTMLDivElement>(null);
  const billsSentinelRef = useRef<HTMLDivElement>(null);

  const fetchVendorBills = useCallback(
    async (
      params: { vendorId?: string; vendorName?: string },
      offset: number = 0,
    ) => {
      const isInitial = offset === 0;
      if (isInitial) {
        setBillsLoading(true);
        setVendorBills([]);
      } else {
        setBillsLoadingMore(true);
      }
      try {
        const token = localStorage.getItem("token");
        const headers: HeadersInit = {
          "Content-Type": "application/json",
          Accept: "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const queryKey = params.vendorId ? "vendor_id" : "vendor";
        const queryValue = params.vendorId
          ? params.vendorId
          : params.vendorName || "";
        const url = `${API_BASE_URL}/api/ap-aging/vendor-bills?${queryKey}=${encodeURIComponent(
          queryValue,
        )}&limit=${BILLS_PAGE_SIZE}&offset=${offset}`;
        const response = await fetch(url, { headers });

        if (!response.ok) {
          throw new Error("Failed to fetch vendor bills");
        }

        const json = await response.json();
        const { data, pagination } = json;

        if (isInitial) {
          setVendorBills(data ?? []);
        } else {
          setVendorBills((prev) => [...prev, ...(data ?? [])]);
        }
        setBillsTotal(pagination?.total ?? 0);
        setBillsHasMore(pagination?.has_more ?? false);
        setBillsOffset(offset + (data?.length ?? 0));
      } catch (err) {
        console.error("Failed to load vendor bills:", err);
        if (isInitial) {
          setVendorBills([]);
        }
      } finally {
        if (isInitial) {
          setBillsLoading(false);
        } else {
          setBillsLoadingMore(false);
        }
      }
    },
    [],
  );

  const fetchMoreBills = useCallback(() => {
    if (
      !billsHasMore ||
      billsLoadingMore ||
      (!selectedVendorId && !selectedVendorName)
    )
      return;
    fetchVendorBills(
      { vendorId: selectedVendorId, vendorName: selectedVendorName },
      billsOffset,
    );
  }, [
    billsHasMore,
    billsLoadingMore,
    selectedVendorId,
    selectedVendorName,
    billsOffset,
    fetchVendorBills,
  ]);

  useEffect(() => {
    if (!drawerOpen || !billsScrollRef.current || !billsSentinelRef.current)
      return;
    if (billsLoading || !billsHasMore || billsLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchMoreBills();
      },
      { root: billsScrollRef.current, rootMargin: "100px", threshold: 0.1 },
    );
    observer.observe(billsSentinelRef.current);
    return () => observer.disconnect();
  }, [
    drawerOpen,
    billsLoading,
    billsHasMore,
    billsLoadingMore,
    fetchMoreBills,
  ]);

  const handleViewBills = (
    vendor: { vendorId?: string; vendorName: string },
    currencySymbol?: string,
  ) => {
    setSelectedVendorId(vendor.vendorId || "");
    setSelectedVendorName(vendor.vendorName);
    setBillCurrencySymbol(currencySymbol?.trim() || "₹");
    setDrawerOpen(true);
    setBillsHasMore(false);
    setBillsOffset(0);
    setBillsTotal(0);
    fetchVendorBills(
      { vendorId: vendor.vendorId, vendorName: vendor.vendorName },
      0,
    );
  };

  const handleDownloadCSV = () => {
    try {
      const rows: string[][] = [];

      // Header
      rows.push(["AP Aging Report", new Date().toLocaleDateString()]);
      rows.push([]);

      // Metrics by Currency
      metrics.forEach((currencyMetrics) => {
        rows.push([
          `Metrics - ${currencyMetrics.currencyCode} (${currencyMetrics.currencySymbol})`,
        ]);
        rows.push([
          "Total Payables",
          formatCurrencyForCSV(currencyMetrics.totalPayables),
          currencyMetrics.currencyCode,
        ]);
        rows.push([
          "Overdue Amount",
          formatCurrencyForCSV(currencyMetrics.overdueAmount),
          currencyMetrics.currencyCode,
        ]);
        rows.push([
          "Due Soon",
          formatCurrencyForCSV(currencyMetrics.dueSoon),
          currencyMetrics.currencyCode,
        ]);
        rows.push([
          "Oldest Unpaid Bill",
          `${currencyMetrics.oldestUnpaidBill} days`,
          "",
        ]);
        rows.push([]);
      });

      // Group distribution by currency
      const distByCurrency: Record<string, APAgingBucket[]> = {};
      distribution.forEach((bucket) => {
        if (!distByCurrency[bucket.currencyCode]) {
          distByCurrency[bucket.currencyCode] = [];
        }
        distByCurrency[bucket.currencyCode].push(bucket);
      });

      // Distribution by Currency
      Object.entries(distByCurrency).forEach(([currencyCode, buckets]) => {
        const currencySymbol = buckets[0]?.currencySymbol || "";
        rows.push([`Aging Distribution - ${currencyCode} (${currencySymbol})`]);
        rows.push(["Range", "Amount", "Currency", "Percentage"]);
        buckets.forEach((bucket) => {
          rows.push([
            `${bucket.range} days`,
            formatCurrencyForCSV(bucket.amount),
            bucket.currencyCode,
            `${bucket.percentage}%`,
          ]);
        });
        rows.push([]);
      });

      // Overdue Vendors
      rows.push(["Overdue Vendors"]);
      rows.push([
        "Vendor Name",
        "Outstanding Amount",
        "Currency",
        "Bill Count",
        "Oldest Age (days)",
        "Status",
      ]);
      overdueVendors.forEach((vendor) => {
        rows.push([
          vendor.vendor,
          formatCurrencyForCSV(vendor.amount),
          vendor.currencyCode,
          vendor.billCount.toString(),
          vendor.oldestAge.toString(),
          vendor.status,
        ]);
      });
      rows.push([]);

      // Due Soon Bills
      rows.push(["Due Soon Bills"]);
      rows.push([
        "Vendor Name",
        "Amount",
        "Currency",
        "Due Date",
        "Age (days)",
      ]);
      dueSoonBills.forEach((bill) => {
        rows.push([
          bill.vendor,
          formatCurrencyForCSV(bill.amount),
          bill.currencyCode,
          bill.dueDate,
          bill.age.toString(),
        ]);
      });

      // Convert to CSV
      const csvContent = rows
        .map((row) =>
          row
            .map((cell) => `"${cell.toString().replace(/"/g, '""')}"`)
            .join(","),
        )
        .join("\n");

      // Download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `ap_aging_report_${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download CSV:", err);
      alert("Failed to download CSV. Please try again.");
    }
  };

  React.useEffect(() => {
    loadData();
  }, []);

  // Reload overdue vendors when pagination changes
  React.useEffect(() => {
    const loadOverdueVendors = async () => {
      try {
        const overdueData = await getOverdueVendors({
          limit: OVERDUE_VENDORS_LIMIT,
          offset: overdueVendorsOffset,
        });
        setOverdueVendors(overdueData.data);
        setOverdueVendorsHasMore(overdueData.pagination.has_more);
        setOverdueVendorsTotal(overdueData.pagination.total);
      } catch (err: any) {
        console.error("Failed to load overdue vendors:", err);
      }
    };

    if (!loading) {
      loadOverdueVendors();
    }
  }, [overdueVendorsOffset]);

  // Reload due soon bills when pagination changes
  React.useEffect(() => {
    const loadDueSoonBills = async () => {
      try {
        const dueSoonData = await getDueSoonBills({
          limit: DUE_SOON_BILLS_LIMIT,
          offset: dueSoonBillsOffset,
        });
        setDueSoonBills(dueSoonData.data);
        setDueSoonBillsHasMore(dueSoonData.pagination.has_more);
        setDueSoonBillsTotal(dueSoonData.pagination.total);
      } catch (err: any) {
        console.error("Failed to load due soon bills:", err);
      }
    };

    if (!loading) {
      loadDueSoonBills();
    }
  }, [dueSoonBillsOffset]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Loading AP Aging data...");

      const [metricsData, distData, overdueData, dueSoonData, advancesData] =
        await Promise.all([
          getMetrics({}),
          getDistribution({}),
          getOverdueVendors({
            limit: OVERDUE_VENDORS_LIMIT,
            offset: overdueVendorsOffset,
          }),
          getDueSoonBills({
            limit: DUE_SOON_BILLS_LIMIT,
            offset: dueSoonBillsOffset,
          }),
          getVendorAdvances({}),
        ]);
      console.log("AP Aging data loaded successfully:", {
        metricsData,
        distData,
        overdueData,
        dueSoonData,
        advancesData,
      });
      setMetrics(metricsData);
      setDistribution(distData.buckets ?? []);
      setDistributionSummary((prev) => ({
        ...prev,
        [distData.currencyCode ?? "INR"]: {
          asOfDate: distData.asOfDate,
          totalOutstandingAmount: distData.totalOutstandingAmount,
          overdueAmount: distData.overdueAmount,
          notOverdueAmount: distData.notOverdueAmount,
          currencySymbol: distData.currencySymbol,
        },
      }));
      setOverdueVendors(overdueData.data);
      setOverdueVendorsHasMore(overdueData.pagination.has_more);
      setOverdueVendorsTotal(overdueData.pagination.total);
      setDueSoonBills(dueSoonData.data);
      setDueSoonBillsHasMore(dueSoonData.pagination.has_more);
      setDueSoonBillsTotal(dueSoonData.pagination.total);
      setVendorAdvances(advancesData);
    } catch (err: any) {
      console.error("Failed to load AP Aging data:", err);
      const errorMessage =
        err?.message || "Failed to load AP Aging data. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getAgingBucketBgClassByRange = (range: string): string | undefined => {
    const normalized = (range ?? "").trim().toLowerCase();
    if (!normalized) return undefined;

    // Not overdue bucket (synthetic or API-provided)
    if (normalized.includes("not overdue")) return "bg-green-500";

    // 91+ (or similar)
    if (normalized.includes("+")) {
      const n = Number((normalized.match(/\d+/) ?? [])[0]);
      if (!Number.isNaN(n) && n >= 91) return "bg-red-600";
    }

    // Parse numeric ranges like "0-30", "31 - 60 days", "61 to 90"
    const nums = (normalized.match(/\d+/g) ?? []).map((n) => Number(n));
    if (nums.length >= 2) {
      const [from, to] = nums;
      if (from <= 0 && to <= 30) return "bg-neutral-500"; // 0–30 grey
      if (from >= 31 && to <= 60) return "bg-blue-500"; // 31–60 blue
      if (from >= 61 && to <= 90) return "bg-yellow-500"; // 61–90 yellow
      if (from >= 91) return "bg-red-600"; // 91+ red (when expressed as range)
    }

    // Fallback: if it contains a single number, map by threshold
    if (nums.length === 1) {
      const [n] = nums;
      if (n <= 30) return "bg-neutral-500";
      if (n <= 60) return "bg-blue-500";
      if (n <= 90) return "bg-yellow-500";
      return "bg-red-600";
    }

    return undefined;
  };

  // Group distribution by currency
  const distributionByCurrency = React.useMemo(() => {
    const grouped: Record<string, APAgingBucket[]> = {};
    distribution.forEach((bucket) => {
      if (!grouped[bucket.currencyCode]) {
        grouped[bucket.currencyCode] = [];
      }
      grouped[bucket.currencyCode].push(bucket);
    });
    return grouped;
  }, [distribution]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-neutral-50">
        <span className="text-body font-body text-subtext-color">
          Loading...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-neutral-50">
        <div className="flex flex-col items-center gap-4">
          <span className="text-body font-body text-error-600">{error}</span>
          <Button onClick={loadData}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 bg-neutral-50 px-6 py-6 overflow-auto">
        <div className="w-full">
          <span className="text-heading-3 font-heading-3 text-subtext-color">
            As of {asOfToday}
          </span>
        </div>
        {/* Header Card */}
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Payables
              </span>
              <span className="text-body font-body text-subtext-color">
                Track vendor payments and upcoming cash obligations.
              </span>
            </div>
            <div className="flex items-center gap-2 no-print">
              <Button
                variant="brand-primary"
                size="medium"
                className="shadow-sm"
                icon={<FeatherDownload className="h-4 w-4" />}
                onClick={handleDownloadCSV}
              >
                Download CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Aging Distribution - Grouped by Currency */}
        {Object.entries(distributionByCurrency).map(
          ([currencyCode, buckets]) => {
            const currencySymbol = buckets[0]?.currencySymbol || "";
            const summary = distributionSummary[currencyCode];
            const displayBuckets = summary
              ? ([
                  {
                    range: "Not overdue",
                    amount: summary.notOverdueAmount ?? 0,
                    percentage:
                      summary.totalOutstandingAmount > 0
                        ? Math.round(
                            (summary.notOverdueAmount /
                              summary.totalOutstandingAmount) *
                              1000,
                          ) / 10
                        : 0,
                    color: "success",
                    currencyCode,
                    currencySymbol: summary.currencySymbol ?? currencySymbol,
                  },
                  ...buckets,
                ] as APAgingBucket[])
              : buckets;
            const adjustedDisplayBuckets = displayBuckets.map((bucket) => ({
              ...bucket,
              color:
                getAgingBucketBgClassByRange(bucket.range) ??
                bucket.color ??
                "bg-neutral-500",
            }));
            const totalAmount =
              summary?.totalOutstandingAmount ??
              adjustedDisplayBuckets.reduce((sum, bucket) => sum + bucket.amount, 0);

            return (
              <div
                key={currencyCode}
                className="flex w-full flex-col items-start gap-4 rounded-xl border border-solid border-neutral-200 bg-white px-8 py-8 shadow-sm"
              >
                <div className="flex w-full items-center justify-between">
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      Payables Ageing Distribution - {currencyCode} (
                      {currencySymbol})
                    </span>
                    <span className="text-body font-body text-subtext-color">
                      Breakdown of overdue bills by age
                    </span>
                    {summary ? (
                      <div className="mt-2 flex w-full flex-wrap items-center gap-x-6 gap-y-2 rounded-lg bg-neutral-50 px-4 py-3">
                        <div className="flex items-baseline gap-2">
                          <span className="h-2.5 w-2.5 flex-none rounded-full bg-neutral-400" />
                          <span className="text-caption font-caption text-subtext-color">
                            Outstanding
                          </span>
                          <span className="text-body-bold font-body-bold text-default-font">
                            {formatPlAmount(
                              summary.totalOutstandingAmount,
                              summary.currencySymbol,
                            )}
                          </span>
                        </div>
                        <div className="hidden h-4 w-px bg-neutral-200 mobile:hidden" />
                        <div className="flex items-baseline gap-2">
                          <span className="h-2.5 w-2.5 flex-none rounded-full bg-success-500" />
                          <span className="text-caption font-caption text-subtext-color">
                            Not overdue
                          </span>
                          <span className="text-body-bold font-body-bold text-default-font">
                            {formatPlAmount(
                              summary.notOverdueAmount,
                              summary.currencySymbol,
                            )}
                          </span>
                        </div>
                        <div className="hidden h-4 w-px bg-neutral-200 mobile:hidden" />
                        <div className="flex items-baseline gap-2">
                          <span className="h-2.5 w-2.5 flex-none rounded-full bg-error-600" />
                          <span className="text-caption font-caption text-subtext-color">
                            Overdue
                          </span>
                          <span className="text-body-bold font-body-bold text-error-700">
                            {formatPlAmount(
                              summary.overdueAmount,
                              summary.currencySymbol,
                            )}
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="flex w-full flex-col items-start gap-3">
                  <div className="flex h-12 w-full flex-none items-center overflow-hidden rounded-lg">
                    {adjustedDisplayBuckets.map((bucket, index) => {
                      const widthPercent =
                        totalAmount > 0
                          ? (bucket.amount / totalAmount) * 100
                          : 0;
                      const showLabel = widthPercent > 8 && bucket.amount > 0;
                      return (
                        <div
                          key={index}
                          className={`flex items-center justify-center self-stretch border-r border-white/70 last:border-r-0 ${
                            bucket.color || "bg-neutral-500"
                          }`}
                          style={{
                            width: `${widthPercent}%`,
                            minWidth: widthPercent > 0 ? "40px" : "0",
                            display: widthPercent > 0 ? "flex" : "none",
                          }}
                        >
                          {showLabel && bucket.amount && (
                            <span className="text-caption-bold font-caption-bold text-white whitespace-nowrap">
                              {formatPlAmount(
                                bucket.amount,
                                bucket.currencySymbol,
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex w-full items-center justify-between flex-wrap gap-2">
                    {adjustedDisplayBuckets.map((bucket, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div
                          className={`flex h-3 w-3 flex-none items-start rounded-sm ${
                            bucket.color || "bg-neutral-500"
                          }`}
                        />
                        <span className="text-caption font-caption text-default-font">
                          {bucket.range === "Not overdue"
                            ? "Not overdue:"
                            : `${bucket.range} days:`}{" "}
                          {formatPlAmount(bucket.amount, bucket.currencySymbol)}{" "}
                          ({bucket.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                {summary && summary.overdueAmount > 0 && (
                  <div className="flex w-full items-center gap-2 rounded-lg border border-solid border-warning-200 bg-warning-50 px-4 py-3">
                    <FeatherAlertTriangle className="text-body font-body text-warning-600" />
                    <span className="text-body font-body text-warning-700">
                      {formatPlAmount(
                        summary.overdueAmount,
                        summary.currencySymbol,
                      )}{" "}
                      of payables are overdue beyond 60 days, which may impact
                      vendor relationships.
                    </span>
                  </div>
                )}
              </div>
            );
          },
        )}

        {/* Metrics Cards - Grouped by Currency */}
        {metrics.map((currencyMetrics, currencyIndex) => (
          <div
            key={currencyMetrics.currencyCode}
            className="flex w-full flex-col gap-4"
          >
            <div className="flex w-full items-center gap-2">
              <span className="text-heading-3 font-heading-3 text-default-font">
                {currencyMetrics.currencyCode} ({currencyMetrics.currencySymbol}
                )
              </span>
            </div>
            <div className="flex w-full gap-4 items-stretch mobile:flex-col mobile:flex-nowrap mobile:gap-4">
              <div className="flex min-w-[192px] grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Total Payables
                  </span>
                  <FeatherCreditCard className="text-body font-body text-brand-600" />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {formatRawAmountWithCommas(
                    currencyMetrics.totalPayables,
                    currencyMetrics.currencyCode,
                    currencyMetrics.currencySymbol,
                  )}
                </span>
                {currencyMetrics.convertedTotalPayables != null &&
                currencyMetrics.convertedCurrencyCode &&
                currencyMetrics.convertedCurrencySymbol ? (
                  <span className="text-caption font-caption text-subtext-color">
                    {formatRawAmountWithCommas(
                      currencyMetrics.convertedTotalPayables,
                      currencyMetrics.convertedCurrencyCode,
                      currencyMetrics.convertedCurrencySymbol,
                    )}{" "}
                    ({currencyMetrics.convertedCurrencyCode})
                  </span>
                ) : null}
                <span className="text-caption font-caption text-subtext-color">
                  Outstanding vendor bills
                </span>
              </div>

              <div className="flex min-w-[192px] grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Overdue Amount
                  </span>
                  <FeatherAlertTriangle className="text-body font-body text-warning-600" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    {formatRawAmountWithCommas(
                      currencyMetrics.overdueAmount,
                      currencyMetrics.currencyCode,
                      currencyMetrics.currencySymbol,
                    )}
                  </span>
                  {currencyMetrics.overdueAmount > 0 && (
                    <Badge variant="warning">Overdue</Badge>
                  )}
                </div>
                {currencyMetrics.convertedOverdueAmount != null &&
                currencyMetrics.convertedCurrencyCode &&
                currencyMetrics.convertedCurrencySymbol ? (
                  <span className="text-caption font-caption text-subtext-color">
                    {formatRawAmountWithCommas(
                      currencyMetrics.convertedOverdueAmount,
                      currencyMetrics.convertedCurrencyCode,
                      currencyMetrics.convertedCurrencySymbol,
                    )}{" "}
                    ({currencyMetrics.convertedCurrencyCode})
                  </span>
                ) : null}
                <span className="text-caption font-caption text-subtext-color">
                  Bills past due date
                </span>
              </div>

              {/* <div className="flex min-w-[192px] grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Due Soon
                  </span>
                  <FeatherCalendarClock className="text-body font-body text-brand-600" />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {formatPlAmount(
                    currencyMetrics.dueSoon,
                    currencyMetrics.currencySymbol,
                  )}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Due in the next 7 days
                </span>
              </div> */}

              <div className="flex min-w-[192px] grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Oldest Unpaid Bill
                  </span>
                  <FeatherClock className="text-body font-body text-error-600" />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {currencyMetrics.oldestUnpaidBill} days
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  Longest unpaid vendor bill
                </span>
              </div>
            </div>
          </div>
        ))}

        {/* Due Soon Bills Table */}
        <div className="flex w-full flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Due Soon
              </span>
              <span className="text-body font-body text-subtext-color">
                Vendor payments due in the next 7 days
              </span>
            </div>
          </div>
          <div className="flex w-full flex-col items-start overflow-hidden overflow-x-auto">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Vendor Name</Table.HeaderCell>
                  <Table.HeaderCell>Amount</Table.HeaderCell>
                  <Table.HeaderCell>Due Date</Table.HeaderCell>
                  <Table.HeaderCell>Age</Table.HeaderCell>
                  <Table.HeaderCell>Action</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {dueSoonBills.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <span className="text-body font-body text-subtext-color">
                      No bills due soon
                    </span>
                  </Table.Cell>
                </Table.Row>
              ) : (
                dueSoonBills.map((bill, index) => {
                  const initials = bill.vendor
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .substring(0, 2)
                    .toUpperCase();
                  const avatarVariant = [
                    "brand",
                    "success",
                    "warning",
                    "neutral",
                  ][index % 4] as any;

                  return (
                    <Table.Row key={index}>
                      <Table.Cell>
                        <div className="flex items-center gap-3">
                          <Avatar
                            variant={avatarVariant}
                            size="small"
                            image=""
                            square={true}
                          >
                            {initials}
                          </Avatar>
                          <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                            {bill.vendor}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                          {formatPlAmount(bill.amount, bill.currencySymbol)}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {bill.dueDate}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {bill.age} days
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherEye />}
                          onClick={() =>
                            handleViewBills(
                              {
                                vendorId: bill.vendorId,
                                vendorName: bill.vendor,
                              },
                              bill.currencySymbol,
                            )
                          }
                        >
                          View bill
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table>
          </div>
          {/* Pagination Controls for Due Soon Bills */}
          {dueSoonBillsTotal > 0 && (
            <div className="flex w-full items-center justify-between gap-4 pt-4">
              <span className="text-body font-body text-subtext-color">
                Showing {dueSoonBillsOffset + 1} to{" "}
                {Math.min(
                  dueSoonBillsOffset + DUE_SOON_BILLS_LIMIT,
                  dueSoonBillsTotal,
                )}{" "}
                of {dueSoonBillsTotal} bills
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="neutral-secondary"
                  size="small"
                  disabled={dueSoonBillsOffset === 0}
                  onClick={() =>
                    setDueSoonBillsOffset(
                      Math.max(0, dueSoonBillsOffset - DUE_SOON_BILLS_LIMIT),
                    )
                  }
                >
                  Previous
                </Button>
                <Button
                  variant="neutral-secondary"
                  size="small"
                  disabled={!dueSoonBillsHasMore}
                  onClick={() =>
                    setDueSoonBillsOffset(
                      dueSoonBillsOffset + DUE_SOON_BILLS_LIMIT,
                    )
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Overdue Payables Table */}
        <div className="flex w-full flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Overdue Payables
              </span>
              <span className="text-body font-body text-subtext-color">
                Vendors with bills overdue beyond 30 days
              </span>
            </div>
          </div>
          <div className="flex w-full flex-col items-start overflow-hidden overflow-x-auto">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Vendor Name</Table.HeaderCell>
                  <Table.HeaderCell>Overdue Amount</Table.HeaderCell>
                  <Table.HeaderCell>Bill Count</Table.HeaderCell>
                  <Table.HeaderCell>Oldest Age</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Action</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {overdueVendors.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <span className="text-body font-body text-subtext-color">
                      No overdue vendors
                    </span>
                  </Table.Cell>
                </Table.Row>
              ) : (
                overdueVendors.map((vendor, index) => {
                  const initials = vendor.vendor
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .substring(0, 2)
                    .toUpperCase();
                  const avatarVariant =
                    vendor.status === "critical" ? "error" : "warning";
                  const ageColor =
                    vendor.oldestAge > 90
                      ? "text-error-600"
                      : vendor.oldestAge > 60
                        ? "text-warning-600"
                        : "text-default-font";

                  return (
                    <Table.Row key={index}>
                      <Table.Cell>
                        <div className="flex items-center gap-3">
                          <Avatar
                            variant={avatarVariant}
                            size="small"
                            image=""
                            square={true}
                          >
                            {initials}
                          </Avatar>
                          <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                            {vendor.vendor}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                          {formatPlAmount(vendor.amount, vendor.currencySymbol)}{" "}
                          ({vendor.currencyCode})
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {vendor.billCount}{" "}
                          {vendor.billCount === 1 ? "bill" : "bills"}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span
                          className={`whitespace-nowrap text-body font-body ${ageColor}`}
                        >
                          {vendor.oldestAge} days
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge
                          variant={
                            vendor.status === "critical" ? "error" : "warning"
                          }
                        >
                          {vendor.status === "critical"
                            ? "Critical"
                            : "Overdue"}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherEye />}
                          onClick={() =>
                            handleViewBills(
                              {
                                vendorId: vendor.vendorId,
                                vendorName: vendor.vendor,
                              },
                              vendor.currencySymbol,
                            )
                          }
                        >
                          View bill
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table>
          </div>
          {/* Pagination Controls for Overdue Vendors */}
          {overdueVendorsTotal > 0 && (
            <div className="flex w-full items-center justify-between gap-4 pt-4">
              <span className="text-body font-body text-subtext-color">
                Showing {overdueVendorsOffset + 1} to{" "}
                {Math.min(
                  overdueVendorsOffset + OVERDUE_VENDORS_LIMIT,
                  overdueVendorsTotal,
                )}{" "}
                of {overdueVendorsTotal} vendors
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="neutral-secondary"
                  size="small"
                  disabled={overdueVendorsOffset === 0}
                  onClick={() =>
                    setOverdueVendorsOffset(
                      Math.max(0, overdueVendorsOffset - OVERDUE_VENDORS_LIMIT),
                    )
                  }
                >
                  Previous
                </Button>
                <Button
                  variant="neutral-secondary"
                  size="small"
                  disabled={!overdueVendorsHasMore}
                  onClick={() =>
                    setOverdueVendorsOffset(
                      overdueVendorsOffset + OVERDUE_VENDORS_LIMIT,
                    )
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Vendor Advances Pending Bills Table */}
        {vendorAdvances.length > 0 && (
          <div className="flex w-full flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-col items-start gap-1">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Vendor Advances Pending Bills
                </span>
                <span className="text-body font-body text-subtext-color">
                  Payments made in advance where vendor bills are still awaited.
                </span>
              </div>
            </div>
            <div className="flex w-full flex-col items-start overflow-hidden overflow-x-auto">
              <Table
                header={
                  <Table.HeaderRow>
                    <Table.HeaderCell>Vendor</Table.HeaderCell>
                    <Table.HeaderCell>Advance Paid</Table.HeaderCell>
                    <Table.HeaderCell>Payment Date</Table.HeaderCell>
                    <Table.HeaderCell>Days Outstanding</Table.HeaderCell>
                    <Table.HeaderCell>Expected Bill Amount</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Recommended Action</Table.HeaderCell>
                  </Table.HeaderRow>
                }
              >
                {vendorAdvances.slice(0, 10).map((advance, index) => {
                  const initials = advance.vendor
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .substring(0, 2)
                    .toUpperCase();
                  const avatarVariant = [
                    "error",
                    "warning",
                    "brand",
                    "success",
                    "neutral",
                  ][index % 5] as any;
                  const ageColor =
                    advance.daysOutstanding > 30
                      ? "text-warning-600"
                      : "text-default-font";

                  return (
                    <Table.Row key={index}>
                      <Table.Cell>
                        <div className="flex items-center gap-3">
                          <Avatar
                            variant={avatarVariant}
                            size="small"
                            image=""
                            square={true}
                          >
                            {initials}
                          </Avatar>
                          <div className="flex flex-col items-start">
                            <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                              {advance.vendor}
                            </span>
                            <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
                              {advance.vendorCode} • {advance.category}
                            </span>
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                          {formatPlAmount(advance.advancePaid)}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {advance.paymentDate}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span
                          className={`whitespace-nowrap text-body font-body ${ageColor}`}
                        >
                          {advance.daysOutstanding} days
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {advance.expectedBillAmount
                            ? formatPlAmount(advance.expectedBillAmount)
                            : "—"}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant="warning">{advance.status}</Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-subtext-color">
                          {advance.recommendedAction}
                        </span>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
          <span className="text-caption font-caption text-subtext-color text-center">
            Figures are based on current books and may change as entries are
            finalized
          </span>
        </div>
      </div>

      {/* Vendor Bills Drawer */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <Drawer.Content className="w-[600px]">
          <div className="flex w-full flex-col items-start gap-6 p-6">
            {/* Header */}
            <div className="flex w-full items-center justify-between shrink-0">
              <div className="flex flex-col items-start gap-1">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Bills for {selectedVendorName}
                </span>
                <span className="text-body font-body text-subtext-color">
                  {billsLoading
                    ? "Loading..."
                    : billsTotal > 0
                      ? `${vendorBills.length} of ${billsTotal} ${billsTotal === 1 ? "bill" : "bills"}`
                      : "No bills found"}
                </span>
              </div>
              <IconButton
                variant="neutral-tertiary"
                size="medium"
                icon={<FeatherX />}
                onClick={() => setDrawerOpen(false)}
              />
            </div>

            {/* Content */}
            {billsLoading ? (
              <div className="flex w-full items-center justify-center py-12">
                <span className="text-body font-body text-subtext-color">
                  Loading bills...
                </span>
              </div>
            ) : vendorBills.length === 0 ? (
              <div className="flex w-full flex-col items-center justify-center py-12 gap-2">
                <FeatherFileText className="text-[32px] text-subtext-color" />
                <span className="text-body font-body text-subtext-color">
                  No bills found for this vendor
                </span>
              </div>
            ) : (
              <div
                ref={billsScrollRef}
                className="flex w-full flex-col items-start gap-4 max-h-[calc(100vh-200px)] overflow-y-auto"
              >
                {vendorBills.map((bill, index) => (
                  <div
                    key={`${bill.billNumber}-${index}`}
                    className="flex w-full flex-col items-start gap-3 rounded-lg border border-solid border-neutral-border bg-default-background p-4"
                  >
                    <div className="flex w-full items-start justify-between">
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Bill #{bill.billNumber}
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          Date: {formatDateDdMmYyyy(bill.date)}
                        </span>
                      </div>
                      <Badge
                        variant={
                          bill.status === "paid"
                            ? "success"
                            : bill.daysOverdue > 0
                              ? "error"
                              : "warning"
                        }
                      >
                        {bill.status === "paid"
                          ? "Paid"
                          : bill.daysOverdue > 0
                            ? `Overdue ${bill.daysOverdue}d`
                            : "Pending"}
                      </Badge>
                    </div>

                    <div className="flex w-full items-center justify-between">
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-caption font-caption text-subtext-color">
                          Amount
                        </span>
                        <span className="text-body-bold font-body-bold text-default-font">
                          {formatPlAmount(bill.amount, billCurrencySymbol)}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-caption font-caption text-subtext-color">
                          Balance
                        </span>
                        <span
                          className={`text-body-bold font-body-bold ${
                            bill.balance > 0
                              ? "text-error-600"
                              : "text-success-600"
                          }`}
                        >
                          {formatPlAmount(bill.balance, billCurrencySymbol)}
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-caption font-caption text-subtext-color">
                          Due Date
                        </span>
                        <span className="text-body font-body text-default-font">
                          {formatDateDdMmYyyy(bill.dueDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={billsSentinelRef} className="h-4 w-full shrink-0" />
                {billsLoadingMore && (
                  <div className="flex w-full items-center justify-center py-4">
                    <span className="text-caption font-caption text-subtext-color">
                      Loading more...
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </Drawer.Content>
      </Drawer>
    </>
  );
}
